import { CommonModule, formatCurrency } from '@angular/common';
import { Component, inject, input, signal, ViewChild, type InputSignal, type WritableSignal } from '@angular/core';
import { CardHeader } from '../../../../shared/components/card/card-header/card-header';
import { Card } from '../../../../shared/components/card/card/card';
import { ButtonModule } from 'primeng/button';
import { UIChart } from 'primeng/chart';
import { DatePickerModule } from 'primeng/datepicker';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { EmptyChart } from '../../../../shared/components/empty-chart/empty-chart';
import { SelectButton } from 'primeng/selectbutton';
import { DashboardService } from '../../../../services/dashboard.service';
import { SocketService } from '../../../../services/socket.service';
import type { ChartConfiguration } from 'chart.js';
import dayjs from 'dayjs';
import { lineChartDefaultOptions } from '../../../../interfaces/charts';
import { SummaryDepositsType, SummaryDepositsInterval, SummaryDepositsMetrics, type SummaryDepositsItemRecord, type DashboardType } from '../../../../interfaces/dashboard';
import { finalize } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { SummaryDepositsTypePipe } from "../../../../shared/pipes/summary-deposits-type-pipe";
import { SummaryDepositsIntervalPipe } from "../../../../shared/pipes/summary-deposits-interval-pipe";
import { SkeletonModule } from 'primeng/skeleton';

@Component({
  selector: 'app-dashboard-pending-deposits',
  imports: [
    CommonModule,
    FormsModule,
    Card,
    CardHeader,
    ButtonModule,
    ProgressSpinnerModule,
    DatePickerModule,
    UIChart,
    EmptyChart,
    SelectModule,
    SelectButton,
    SummaryDepositsTypePipe,
    SummaryDepositsIntervalPipe,
    SkeletonModule
],
  templateUrl: './dashboard-pending-deposits.html',
  styleUrl: './dashboard-pending-deposits.css',
})
export class DashboardPendingDeposits {
  @ViewChild('pixConversionRateIndividualChart') pixConversionRateIndividualChart?: UIChart;
  @ViewChild('pixConversionRateAccumulatedChart') pixConversionRateAccumulatedChart?: UIChart;

  private readonly _dashboardService: DashboardService = inject(DashboardService);
  private readonly _socketService: SocketService = inject(SocketService);

  public startDateFilter: InputSignal<Date|null> = input<Date|null>(null);
  public endDateFilter: InputSignal<Date|null> = input<Date|null>(null);

  public summaryDepositsType: typeof SummaryDepositsType = SummaryDepositsType;
  public summaryDepositsShowType: SummaryDepositsType = SummaryDepositsType.INDIVIDUAL;
  public summaryDepositsInterval: typeof SummaryDepositsInterval = SummaryDepositsInterval;
  public summaryDepositsIntervalSelected: SummaryDepositsInterval = SummaryDepositsInterval['10M'];

  public summaryDepositsData: WritableSignal<SummaryDepositsMetrics|null> = signal<SummaryDepositsMetrics|null>(null);
  public summaryDepositsLoading: WritableSignal<boolean> = signal<boolean>(false);
  public summaryDepositsDate: Date = dayjs().toDate();
  public summaryDepositsChartData: WritableSignal<any> = signal<any>(null);
  public summaryDepositsChartOptions: WritableSignal<ChartConfiguration["options"]> = signal<ChartConfiguration["options"]>({...lineChartDefaultOptions});

  public ngOnInit(): void {
    this._initSocket();
  }

  private _initSocket(): void {
    this._socketService.echo
      .channel('dashboard')
      .listen('.updated', (data: { dashboard: DashboardType, data: any }) => {
        this.summaryDepositsData.set(data.data as SummaryDepositsMetrics);
        this.mountSummaryDepositsData();
      });
  }

  private _initDashboard(): void {
    this.getSummaryDeposits();
    this.mountSummaryDepositsOptions();
  }

  public ngOnChanges(): void {
    if(!this._isCompletedRangeDates()) {
      return;
    }

    this.summaryDepositsDate = this.endDateFilter()!;

    this._initDashboard();
  }

  private _isCompletedRangeDates(): boolean {
    return this.startDateFilter() instanceof Date && this.endDateFilter() instanceof Date;
  }

  private _recordSummaryDeposistsTitle = (record: string): string => {
    switch (record) {
      case 'current': return "Atual";
      case 'minus_7_days': return "D-7";
      case 'minus_14_days': return "D-14";
      default: return "";
    }
  }

  public getSummaryDeposits(): void {
    this.summaryDepositsLoading.set(true);

    const filters = {
      date: dayjs(this.endDateFilter()).format('YYYY-MM-DD'),
      interval: this.summaryDepositsIntervalSelected
    };

    this._dashboardService.summaryDeposits(filters)
      .pipe(finalize(() => this.summaryDepositsLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.summaryDepositsData.set(response.data);
            this.mountSummaryDepositsData();
          }
        }
      });
  }

  public mountSummaryDepositsData(): void {
    if (this.summaryDepositsShowType === SummaryDepositsType.ACCUMULATED) {
      this.mountSummaryDeposistsAccumulatedData();
    }

    if (this.summaryDepositsShowType === SummaryDepositsType.INDIVIDUAL) {
      this.mountSummaryDeposistsIndividualData();
    }
  }


  public mountSummaryDepositsOptions(): void {
    if (this.summaryDepositsShowType === SummaryDepositsType.ACCUMULATED) {
      this.mountSummaryDepositsAccumulatedOptions();
    }

    if (this.summaryDepositsShowType === SummaryDepositsType.INDIVIDUAL) {
      this.mountSummaryDepositsIndividualOptions();
    }
  }

  public mountSummaryDeposistsIndividualData(): any {
    this.summaryDepositsChartData.set({
      labels: this.summaryDepositsData()?.current.records?.map((record: SummaryDepositsItemRecord) => dayjs(record.date_time).format('HH:mm')) || [],
      datasets: Object.entries(this.summaryDepositsData() || {}).filter(([key, value]) => value.records).map(([key, value]) => ({
        label: this._recordSummaryDeposistsTitle(key),
        data: value.records?.map((record: SummaryDepositsItemRecord) => record.pending_tax) || [],
        fill: false,
        tension: 0.2,
        borderWidth: 2,
        pointRadius: 2,
        customData: value.records
      }))
    });

    this.pixConversionRateIndividualChart?.refresh();
  }

  public mountSummaryDepositsIndividualOptions(): void {
    this.summaryDepositsChartOptions.set({
      ...lineChartDefaultOptions,
      maintainAspectRatio: false,
      scales: {
        ...lineChartDefaultOptions?.scales,
        y: {
          grid: {
            display: true,
          },
          ticks: {
            callback: function (value, index, ticks) {
              return value + '%';
            }
          },
          beginAtZero: true,
          max: 100
        }
      },
      plugins: {
        ...lineChartDefaultOptions?.plugins,
        tooltip: {
          ...lineChartDefaultOptions?.plugins?.tooltip,
          callbacks: {
            label: (context: any) => {
              const data = Math.round(context.raw);
              const pending = formatCurrency(Number(context.dataset.customData[context.dataIndex].value_pending), 'pt_BR', 'R$');
              const approval = formatCurrency(Number(context.dataset.customData[context.dataIndex].value_approved), 'pt_BR', 'R$');
              const conversion = Math.round(context.dataset.customData[context.dataIndex].conversion);

              return [
                context.dataset.label,
                'Pendência: ' + data + '%',
                'Conversão: ' + conversion + '%',
                'Pendente: ' + pending,
                'Aprovado: ' + approval
              ];
            }
          }
        }
      }
    });
  }

  public mountSummaryDeposistsAccumulatedData(): any {
    this.summaryDepositsChartData.set({
      labels: this.summaryDepositsData()?.current.records?.map((record: SummaryDepositsItemRecord) => dayjs(record.date_time).format('HH:mm')) || [],
      datasets: Object.entries(this.summaryDepositsData() || {}).filter(([key, value]) => value.records).map(([key, value]) => ({
        label: this._recordSummaryDeposistsTitle(key),
        data: value.records?.map((record: SummaryDepositsItemRecord) => record.accumulated_pending_tax) || [],
        fill: false,
        tension: 0.2,
        borderWidth: 2,
        pointRadius: 2,
        customData: value.records
      }))
    });

    this.pixConversionRateAccumulatedChart?.refresh();
  }

  public mountSummaryDepositsAccumulatedOptions(): void {
    this.summaryDepositsChartOptions.set({
      ...lineChartDefaultOptions,
      maintainAspectRatio: false,
      scales: {
        ...lineChartDefaultOptions?.scales,
        y: {
          grid: {
            display: true,
          },
          ticks: {
            callback: function (value, index, ticks) {
              return value + '%';
            }
          },
          beginAtZero: true,
          max: 100
        }
      },
      plugins: {
        ...lineChartDefaultOptions?.plugins,
        tooltip: {
          ...lineChartDefaultOptions?.plugins?.tooltip,
          callbacks: {
            label: (context: any) => {
              const data = context.raw;
              const pending = formatCurrency(Number(context.dataset.customData[context.dataIndex].accumulated_value_pending), 'pt_BR', 'R$');
              const approval = formatCurrency(Number(context.dataset.customData[context.dataIndex].accumulated_value_approved), 'pt_BR', 'R$');
              const conversion = context.dataset.customData[context.dataIndex].conversion;

              return [
                context.dataset.label,
                'Pendência: ' + data + '%',
                'Conversão: ' + conversion + '%',
                'Pendente: ' + pending,
                'Aprovado: ' + approval
              ];
            }
          }
        }
      }
    });
  }

}
